import { useEffect, useMemo, useState } from 'react';
import { Button, Spinner, Text, Caption1, mergeClasses } from '@fluentui/react-components';
import { Edit16Regular, Delete16Regular, Dismiss16Regular, CheckmarkCircle16Regular, Person16Regular, DoorTag20Regular, Add16Regular } from '@fluentui/react-icons';
import { connector, Props, useStyles } from './ConsultoriosList.Functions';
import './ConsultoriosList.Styles.css';
import ConsultorioFormComponent from '../Consultorio.Form/ConsultorioForm.Component';
import ConfirmationModalComponent from '../../../../commons/component/ConfirmationModal/ConformationModal.Component';

type StatusKey = 'libre' | 'ocupado' | 'reservado' | 'nodisp' | 'default';

const getStatusKey = (status?: string | null): StatusKey => {
  const normalized = (status || '').toLowerCase();
  if (normalized.includes('no disp') || normalized.includes('deshab')) return 'nodisp';
  if (normalized.includes('ocup')) return 'ocupado';
  if (normalized.includes('reser')) return 'reservado';
  if (normalized.includes('libre') || normalized.includes('dispon')) return 'libre';
  return 'default';
};

const statusAccentClass = (key: StatusKey, classes: ReturnType<typeof useStyles>): string => {
  switch (key) {
    case 'libre': return classes.statusLibre;
    case 'ocupado': return classes.statusOcupado;
    case 'reservado': return classes.statusReservado;
    case 'nodisp': return classes.statusNoDisponible;
    default: return '';
  }
};

const statusBadgeClass = (key: StatusKey, classes: ReturnType<typeof useStyles>): string => {
  switch (key) {
    case 'libre': return classes.cardStatusBadgeLibre;
    case 'ocupado': return classes.cardStatusBadgeOcupado;
    case 'reservado': return classes.cardStatusBadgeReservado;
    case 'nodisp': return classes.cardStatusBadgeNoDisp;
    default: return '';
  }
};

const ConsultoriosListComponent = ({ sectorId, consultorios, sectores, isLoading, failOnLoading, error, isDeleting, isUpdatingEstado, GetConsultorios, DeleteConsultorio, UpdateEstadoConsultorio }: Props) => {
  const styles = useStyles();
  const [isFormOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingSectorId, setEditingSectorId] = useState<number | null>(null);

  useEffect(() => {
    GetConsultorios();
  }, [GetConsultorios, sectorId]);

  useEffect(() => {
    setFormOpen(false);
    setEditingId(null);
    setEditingSectorId(null);
  }, [sectorId]);

  const consultoriosFiltrados = useMemo(() => {
    return (consultorios || []).filter((c) => (sectorId ? c.sectorId === sectorId : true));
  }, [consultorios, sectorId]);

  const branchId = useMemo(() => {
    const targetSectorId = editingSectorId ?? sectorId;
    if (targetSectorId === undefined || targetSectorId === null) return undefined;
    const found = (sectores || []).find((s) => s.id === targetSectorId);
    return found?.branchId ?? found?.branch?.id;
  }, [sectores, editingSectorId, sectorId]);

  const headerSubtitle = useMemo(() => {
    if (sectorId === undefined || sectorId === null) return 'Seleccione un sector para gestionar consultorios';
    const suffix = consultoriosFiltrados.length === 1 ? '' : 's';
    return `${consultoriosFiltrados.length} consultorio${suffix} en el sector`;
  }, [sectorId, consultoriosFiltrados.length]);

  const renderGrid = () => (
    <div className={styles.grid}>
      {consultoriosFiltrados.map((c) => {
        const statusKey = getStatusKey(c.status);
        const accentClass = statusAccentClass(statusKey, styles);
        const badgeClass = statusBadgeClass(statusKey, styles);
        const doctorName = c.assignedDoctorFullName || c.doctor?.fullName;
        const consultorioLabel = c.name || c.number || 'este consultorio';
        const isDisabled = statusKey === 'nodisp';
        return (
          <div key={c.id ?? c.number} className={mergeClasses(styles.card, accentClass, 'consultorio-card')}>
            <div className={styles.cardTopRow}>
              {c.number && <span className={styles.cardNumber}>#{c.number}</span>}
              <span className={mergeClasses(styles.cardStatusBadge, badgeClass)}>
                {c.status || 'Sin estado'}
              </span>
            </div>
            <div className={styles.cardName} title={c.name || 'Consultorio'}>
              {c.name || 'Consultorio'}
            </div>
            <div className={styles.doctorRow}>
              <Person16Regular style={{ flexShrink: 0, color: 'var(--colorNeutralForeground3)' }} />
              <span
                className={doctorName ? styles.doctorName : styles.doctorEmpty}
                title={doctorName || 'Sin médico asignado'}
              >
                {doctorName || 'Sin médico asignado'}
              </span>
            </div>
            <div className="consultorio-card-actions">
              <Button
                size="small"
                appearance="subtle"
                icon={<Edit16Regular />}
                title="Editar consultorio"
                onClick={() => {
                  setEditingId(c.id ?? null);
                  setEditingSectorId(c.sectorId ?? sectorId ?? null);
                  setFormOpen(true);
                }}
              />
              <ConfirmationModalComponent
                onDismiss={() => {}}
                onConfirm={() => {
                  if (c.id !== undefined && c.id !== null) {
                    const nextEstado = isDisabled ? 'Disponible' : 'No disponible';
                    UpdateEstadoConsultorio(c.id, nextEstado);
                  }
                }}
                title={isDisabled ? 'Habilitar consultorio' : 'Deshabilitar consultorio'}
                message={`¿Desea ${isDisabled ? 'habilitar' : 'deshabilitar'} el consultorio ${consultorioLabel}?`}
                buttonApparence="subtle"
                buttonShape="circular"
                buttonTitle=""
                buttonIcon={isDisabled ? <CheckmarkCircle16Regular /> : <Dismiss16Regular />}
                isProcessing={isUpdatingEstado}
              />
              <ConfirmationModalComponent
                onDismiss={() => {}}
                onConfirm={() => {
                  if (c.id !== undefined && c.id !== null) {
                    DeleteConsultorio(c.id);
                  }
                }}
                title="Eliminar consultorio"
                message={`¿Desea eliminar el consultorio ${consultorioLabel}?`}
                buttonApparence="subtle"
                buttonShape="circular"
                buttonTitle=""
                buttonIcon={<Delete16Regular />}
                isProcessing={isDeleting}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="consultorios-list-wrapper">
      <div className="consultorios-list-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DoorTag20Regular style={{ color: '#0f6cbd', flexShrink: 0 }} />
            <Text weight="semibold" size={400}>Consultorios</Text>
          </div>
          <Caption1 style={{ color: 'var(--colorNeutralForeground3)' }}>
            {headerSubtitle}
          </Caption1>
        </div>
        <Button
          size="medium"
          appearance="primary"
          icon={<Add16Regular />}
          onClick={() => setFormOpen(true)}
          disabled={sectorId === undefined || sectorId === null}
          title={sectorId === undefined || sectorId === null ? 'Seleccione un sector para agregar un consultorio' : undefined}
        >
          Nuevo consultorio
        </Button>
      </div>

      {(() => {
        if (isLoading) {
          return (
            <div className="consultorios-list-loading">
              <Spinner label="Cargando consultorios" />
            </div>
          );
        }

        if (failOnLoading) {
          return (
            <div className="consultorios-list-error">
              <Text>{error?.ErrorMessage || 'No se pudieron cargar los consultorios'}</Text>
            </div>
          );
        }

        if (consultoriosFiltrados.length === 0) {
          return (
            <div className="consultorios-list-empty">
              <DoorTag20Regular style={{ width: 28, height: 28, color: 'var(--colorNeutralForeground4)' }} />
              <Text weight="semibold">Sin consultorios</Text>
              <Caption1 style={{ color: 'var(--colorNeutralForeground3)' }}>
                {sectorId ? 'Aún no hay consultorios en este sector' : 'Seleccione un sector en el panel izquierdo'}
              </Caption1>
            </div>
          );
        }

        return renderGrid();
      })()}

      <ConsultorioFormComponent
        isOpen={isFormOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingId(null);
          setEditingSectorId(null);
        }}
        defaultSectorId={(editingSectorId ?? sectorId) ?? 0}
        branchId={branchId}
        consultorioId={editingId ?? undefined}
      />
    </div>
  );
};

export default connector(ConsultoriosListComponent as any);
